import React, { useMemo, useState } from 'react';
import { Badge, Button, Combobox, InlineStack, Listbox, Tag, Text, Tooltip } from '@shopify/polaris';

const MAX_SHOWN = 100;   // a 131-size list stays usable by filtering; only this many options render at once

// A list-valued facet: chips for the current values (a pending one, which is not on Shopify's list, in the warning tone)
// and "+ add" over the allowed values: a Combobox, so arrow keys move through the filtered options and Enter picks.
// Every change hands the FULL list to onChange.
export default function MultiValueCell({ label, values, details, allowed, disabled, onChange }) {
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const pendingBy = useMemo(() => Object.fromEntries((details || []).filter((x) => x.pending).map((x) => [x.value, x])), [details]);
  const options = useMemo(() => {
    const have = new Set(values);
    const needle = q.trim().toLowerCase();
    return (allowed || []).filter((a) => !have.has(a) && (!needle || a.toLowerCase().includes(needle)));
  }, [allowed, values, q]);
  const remove = (v) => onChange(values.filter((x) => x !== v));
  const close = () => { setAdding(false); setQ(''); };
  const add = (v) => { close(); onChange([...values, v]); };
  return (
    <InlineStack gap="100" blockAlign="center" wrap>
      {values.length === 0 && <Text as="span" tone="subdued"><em>none</em></Text>}
      {values.map((v) => {
        const p = pendingBy[v];
        if (!p) return <Tag key={v} disabled={disabled} onRemove={() => remove(v)}>{v}</Tag>;
        const by = (p.sources || []).join(', ') || 'a source';
        return (
          <Tooltip key={v} content={`Reported by ${by}; not one of Shopify's values. Accept it in the Dictionary or drop it.`}>
            <InlineStack gap="050" blockAlign="center" wrap={false}>
              <Badge tone="warning">{`${v} · pending`}</Badge>
              <Button size="micro" variant="plain" accessibilityLabel={`Drop ${v}`} disabled={disabled} onClick={() => remove(v)}>×</Button>
            </InlineStack>
          </Tooltip>
        );
      })}
      {adding ? (
        <div style={{ minWidth: 240 }}>
          <Combobox allowMultiple={false}
            activator={<Combobox.TextField label={`Add ${label}`} labelHidden value={q} onChange={setQ} autoComplete="off" autoFocus
              placeholder={`Pick from Shopify's ${(allowed || []).length} values`} onBlur={() => { if (!q) setAdding(false); }} />}
>
            {options.length > 0 ? (
              <Listbox accessibilityLabel={`${label} values`} onSelect={add}>
                {options.slice(0, MAX_SHOWN).map((o) => <Listbox.Option key={o} value={o}>{o}</Listbox.Option>)}
                {options.length > MAX_SHOWN && <Listbox.Loading accessibilityLabel={`${options.length - MAX_SHOWN} more; keep typing`} />}
              </Listbox>
            ) : <div style={{ padding: 12 }}><Text as="p" tone="subdued">No value matches.</Text></div>}
          </Combobox>
          {options.length > MAX_SHOWN && <Text as="p" variant="bodySm" tone="subdued">{options.length - MAX_SHOWN} more; keep typing to narrow.</Text>}
        </div>
      ) : <Button size="micro" variant="plain" disabled={disabled} onClick={() => setAdding(true)}>+ add</Button>}
    </InlineStack>
  );
}
